<template>
  <Transition name="fly-from-bottom" appear @before-enter="show = true">
    <div
      class="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-white/90 dark:tw-bg-black/90"
    >
      <Transition name="opacity" appear @after-leave="$emit('close')">
        <div
          v-if="show"
          class="tw-m-2 tw-flex tw-w-full tw-flex-col tw-rounded-2xl tw-border tw-bg-white tw-shadow-2xl dark:tw-bg-black"
          :class="{
            'tw-h-fit-content tw-max-h-[90vh] tw-w-full tw-max-w-6xl tw-overflow-auto': size === '2xlarge',
            'tw-h-full tw-max-w-4xl md:tw-max-h-[500px]': size === 'xlarge',
            'tw-h-full tw-max-w-2xl md:tw-max-h-[500px]': size === 'large',
            'tw-h-full tw-max-h-[90vh] tw-max-w-2xl md:tw-h-fit': size === 'h-fit',
            'tw-max-w-xl': size === 'medium',
            'tw-max-w-lg': size === 'small',
            'tw-max-w-md': size === 'xsmall'
          }"
        >
          <div
            v-if="showCloseButton || title || $slots.title"
            :class="{
              'tw-border-b': title || $slots.title
            }"
          >
            <div class="tw-flex tw-items-center tw-justify-between tw-p-3">
              <h2 v-if="title" id="studio-dialog-title" class="tw-text-xs tw-font-medium dark:tw-text-white">
                {{ title }}
              </h2>
              <slot name="title" />
              <button
                v-if="showCloseButton"
                :disabled="actionsDisabled"
                type="button"
                class="tw-ml-auto"
                data-testid="studio-dialog-cross-button"
                @click="handleClose"
              >
                <TablerX class="tw-ml-auto tw-h-4 tw-w-4 dark:tw-text-white" />
              </button>
            </div>
          </div>
          <slot />
          <div v-if="showFooter" class="tw-item-center tw-flex tw-items-center tw-justify-end tw-border-t tw-p-2">
            <slot name="footer-content" />
            <StudioButton
              v-if="secondaryActionTitle"
              :variant="secondaryActionVariant"
              trackingId="studio-dialog-secondary-button"
              :disabled="actionsDisabled || secondaryActionDisabled"
              @click="$emit('secondaryAction')"
            >
              {{ secondaryActionTitle }}
            </StudioButton>
            <StudioButton
              v-if="primaryActionTitle"
              :variant="primaryActionVariant"
              trackingId="studio-dialog-primary-button"
              type="submit"
              class="tw-ml-2"
              :disabled="actionsDisabled || primaryActionDisabled"
              @click="$emit('primaryAction')"
            >
              <slot name="primaryActionIcon" :className="primaryActionIconClass" />
              {{ primaryActionTitle }}
            </StudioButton>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import TablerX from './Icons/tabler/TablerX.vue';
import StudioButton from './StudioButton.vue';

export type StudioButtonVariant = 'filled' | 'outline' | 'text' | 'warn';

export default defineComponent({
  name: 'StudioDialog',
  components: {
    StudioButton,
    TablerX
  },
  props: {
    title: { type: String, required: false },
    primaryActionTitle: { type: String, default: '' },
    primaryActionVariant: {
      type: String as () => StudioButtonVariant,
      default: 'filled',
      validator: (value: string) => ['filled', 'outline', 'text', 'warn'].includes(value)
    },
    secondaryActionVariant: {
      type: String as () => StudioButtonVariant,
      default: 'text',
      validator: (value: string) => ['filled', 'outline', 'text', 'warn'].includes(value)
    },
    primaryActionDisabled: { type: Boolean, default: false },
    secondaryActionTitle: { type: String, default: '' },
    secondaryActionDisabled: { type: Boolean, default: false },
    actionsDisabled: { type: Boolean, default: false },
    preventClose: { type: Boolean, default: false },
    preventCloseMessage: {
      type: String,
      default: 'Are you sure you want to close this dialog?'
    },
    size: {
      type: String as PropType<'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | '2xlarge' | 'h-fit'>,
      default: 'medium',
      validator: (prop: string) => ['xsmall', 'small', 'medium', 'large', 'xlarge', '2xlarge', 'h-fit'].includes(prop)
    },
    showCloseButton: {
      type: Boolean,
      default: true
    },
    primaryActionIconClass: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'primaryAction', 'secondaryAction'],
  data() {
    return {
      show: false
    };
  },
  computed: {
    showFooter(): boolean {
      if (this.primaryActionTitle || this.secondaryActionTitle) {
        return true;
      }

      return false;
    }
  },
  methods: {
    handleClose() {
      if (this.preventClose && !window.confirm(this.preventCloseMessage)) {
        return;
      }

      this.show = false;
    }
  }
});
</script>

<style scoped>
.fly-from-bottom-enter-active,
.fly-from-bottom-leave-active {
  transition: transform 0.25s;
}
.fly-from-bottom-enter-from,
.fly-from-bottom-leave-to {
  transform: translateY(100px);
}

.opacity-enter-active,
.opacity-leave-active {
  transition: opacity 0.25s;
}

.opacity-enter-from,
.opacity-leave-to {
  opacity: 0;
}
</style>
